// Shared helper functions used across LawFirmApproveBonds / LawFirmCancelBonds /
// LawFirmEndorseBonds (and ApproveBondsTeamMember). Extracted to remove
// repeated cognitive complexity and duplicated logic in those page files.

import { apiClient } from "../../../api/httpClient";
import {
  DWB_LABEL_MAP,
  getReleaseRiderUrl,
  isNameChangeEndorsement,
} from "../../hooks/bondsEndorsementSharedConfig";
import { applyCommonBondListFilters } from "../../hooks/bondsTableFilterHelpers";
import { parseCurrencyToNumber } from "../../../features/bonds/shared/formHelpers";

export { DWB_LABEL_MAP, getReleaseRiderUrl, isNameChangeEndorsement };

/** Serialize display currency for POST /endorse-bond/ updated_bond_amount (preserves decimals). */
export const formatUpdatedBondAmountForEndorsement = (displayValue) => {
  const n = parseCurrencyToNumber(displayValue);
  return Number.isFinite(n) ? String(n) : "";
};

// Title-case helper used when normalizing the county name for endorsement.
const titleCaseWords = (s) =>
  String(s || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

// Append the DWB name fields (single-name vs split-name).
const appendDwbNameFields = (formData, payload) => {
  const {
    selectedBondToEndorse,
    updatedName,
    dwbFirstName,
    dwbMiddleName,
    dwbLastName,
    dwbSuffix,
    dwbAlias,
  } = payload;
  if (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single) {
    formData.append("updated_name", updatedName);
    return;
  }
  formData.append(
    "updated_name",
    [dwbFirstName, dwbMiddleName, dwbLastName, dwbSuffix].filter(Boolean).join(" ")
  );
  formData.append("updated_first_name", dwbFirstName);
  formData.append("updated_middle_name", dwbMiddleName);
  formData.append("updated_last_name", dwbLastName);
  formData.append("updated_suffix", dwbSuffix);
  formData.append("updated_alias", dwbAlias);
};

// Append the name-change related fields based on endorsement type.
const appendNameChangeFields = (formData, payload) => {
  const { endorsementType, endorsementToCounty, updatedName } = payload;
  if (endorsementType === "Name_Change_County") {
    formData.append("updated_name", titleCaseWords(endorsementToCounty));
    return;
  }
  if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
    appendDwbNameFields(formData, payload);
    return;
  }
  formData.append("updated_name", updatedName);
  const { selectedBondToEndorse, endorsementPrincipalId } = payload;
  if (endorsementType === "Name_Change_Principal" && endorsementPrincipalId) {
    formData.append("principal_id", String(endorsementPrincipalId));
    if (selectedBondToEndorse?.bond_type === "Trustee") {
      formData.append("trustee_id", String(endorsementPrincipalId));
    }
  }
};

// Append the optional estimated_assets number for Probate / Guardianship bonds.
const appendEstimatedAssets = (formData, selectedBondToEndorse, estimatedAssets) => {
  const numericValue = String(estimatedAssets || "").replaceAll(/[^\d.]/g, "");
  const parsed = numericValue !== "" && !Number.isNaN(Number(numericValue))
    ? Number(numericValue)
    : null;
  const bondType = selectedBondToEndorse?.bond_type;
  if ((bondType === "Probate" || bondType === "Guardianship") && parsed != null) {
    formData.append("estimated_assets", parsed);
  }
};

// Build the FormData payload for an endorsement (bond amount or name change).
export const buildEndorsementFormData = (payload) => {
  const {
    endorsementType,
    endorsementFile,
    contactName,
    contactEmail,
    newBondAmount,
    selectedBondToEndorse,
    estimatedAssets,
  } = payload;

  const formData = new FormData();
  formData.append("endorsement_type", endorsementType);

  if (isNameChangeEndorsement(endorsementType)) {
    appendNameChangeFields(formData, payload);
  } else {
    formData.append(
      "updated_bond_amount",
      formatUpdatedBondAmountForEndorsement(newBondAmount)
    );
  }

  if (endorsementFile) formData.append("endorsement_file", endorsementFile);
  formData.append("contact_name", contactName);
  formData.append("contact_email", contactEmail);
  appendEstimatedAssets(formData, selectedBondToEndorse, estimatedAssets);

  return formData;
};

// Compute whether the name-change endorsement form is valid (county / DWB / principal).
export const computeNameChangeOk = (state) => {
  const {
    endorsementType,
    endorsementToCounty,
    updatedName,
    selectedBondToEndorse,
    dwbFirstName,
    dwbLastName,
  } = state;
  if (endorsementType === "Name_Change_County") {
    return !!endorsementToCounty;
  }
  if (endorsementType === "Name_Change_Principal") {
    return !!String(updatedName || "").trim();
  }
  if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
    const labels = DWB_LABEL_MAP[selectedBondToEndorse?.bond_type] || DWB_LABEL_MAP.Probate;
    if (labels.single) {
      return !!String(updatedName || "").trim();
    }
    return !!String(dwbFirstName || "").trim() && !!String(dwbLastName || "").trim();
  }
  return true;
};

// Apply the standard bond-list filters used across LawFirm bonds pages.
export const applyBondFilters = (rows, f) => {
  let filtered = applyCommonBondListFilters(rows, f, { includeUnderwriting: true });

  if (f.principal?.length) {
    filtered = filtered.filter((b) =>
      f.principal.some((x) => (b.principals || []).includes(x.label))
    );
  }

  return filtered;
};

export const normalizePaymentStatusKey = (raw) => {
  if (raw == null || String(raw).trim() === "") return "unknown";
  const s = String(raw).toLowerCase().trim();
  if (s === "paid") return "paid";
  if (s === "unpaid") return "unpaid";
  if (s === "na" || s === "n/a") return "na";
  if (s === "unknown") return "unknown";
  return "unknown";
};

export function formatPaymentStatusExportLabel(raw) {
  const key = normalizePaymentStatusKey(raw);
  if (key === "paid") return "Paid";
  if (key === "unpaid") return "Unpaid";
  if (key === "na") return "N/A";
  return "Unknown";
}

/** Lowercase search tokens used when filtering Bond Archives by payment status. */
export function getPaymentStatusSearchText(raw) {
  const key = normalizePaymentStatusKey(raw);
  if (key === "paid") return "paid";
  if (key === "unpaid") return "unpaid";
  if (key === "na") return "n/a na";
  return "unknown";
}

export const bondMatchesPaymentStatusFilter = (bond, filterValue) => {
  if (!filterValue) return true;
  const key = normalizePaymentStatusKey(bond.payment_status);
  if (filterValue === "Paid") return key === "paid";
  if (filterValue === "Unpaid") return key === "unpaid";
  if (filterValue === "Unknown") return key === "unknown";
  if (filterValue === "N/A") return key === "na";
  return true;
};

/** Principal filter matching used on Bond Archives (comma-separated principals string). */
export const bondMatchesArchivesPrincipalFilter = (bond, principalFilters) => {
  const principals = Array.isArray(bond.principals)
    ? bond.principals
    : (bond.principals || "").split(",").map((p) => p.trim());
  return principalFilters.some((filter) =>
    principals.some((p) => p.toLowerCase() === filter.label.toLowerCase())
  );
};

/**
 * Bond Archives page filtering (sync + PG async narrowing), shared with hook refetch flow.
 */
export async function applyBondArchivesFilters(rows, filters) {
  const f = filters || {};
  let result = applyCommonBondListFilters(rows, f);

  if (f.professionalGuardian?.length) {
    const guardianIds = f.professionalGuardian.map((g) => g.value);
    const guardianBondsArrays = await Promise.all(
      guardianIds.map((gid) => apiClient(`/list-guardian-bonds/${gid}/`, "GET"))
    );
    const allowedIds = new Set(guardianBondsArrays.flat().map((gb) => gb.bond_id));
    result = result.filter((b) => allowedIds.has(b.bond_id));
  }

  if (f.principal?.length) {
    result = result.filter((b) => bondMatchesArchivesPrincipalFilter(b, f.principal));
  }
  if (f.internalUser?.length) {
    result = result.filter((b) =>
      f.internalUser.some(
        (x) => Array.isArray(b.managed_by) && b.managed_by.includes(x.label)
      )
    );
  }
  if (f.payment_status) {
    result = result.filter((b) => bondMatchesPaymentStatusFilter(b, f.payment_status));
  }

  return result;
}

export const capitalizeFirst = (s) => {
  const str = (s ?? "").toString().trim();
  return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;
};

/** Team-member underwriting archives — preserves ApproveBondsTeamMember filter behavior. */
export const applyArchiveBondFilters = async (rows, f) => {
  let result = applyCommonBondListFilters(rows, f, { includeUnderwriting: true });

  if (f.professionalGuardian?.length) {
    const guardianIds = f.professionalGuardian.map((g) => g.value);
    const guardianBondsArrays = await Promise.all(
      guardianIds.map((gid) => apiClient(`/list-guardian-bonds/${gid}/`, "GET"))
    );
    const allowedIds = new Set(guardianBondsArrays.flat().map((gb) => gb.bond_id));
    result = result.filter((b) => allowedIds.has(b.bond_id));
  }
  if (f.principal?.length) {
    result = result.filter((b) => bondMatchesArchivesPrincipalFilter(b, f.principal));
  }

  return result;
};

export const formatArchiveBondRow = (b) => ({
  bond_id: b.bond_id,
  bond_number: b.bond_number || "",
  bond_type: b.bond_type,
  bond_sub_category: b.bond_sub_category,
  principals: b.principals || [],
  estate_name: b.name,
  law_firm_name: b.law_firm_name,
  attorney_name: b.law_firm_user_name,
  underwriting_status: (b.underwriting_status || "-").toString().trim(),
  bond_amount: b.bond_amount || "0.00",
  status: b.status || "Active",
  created_at: b.created_at,
});

// Map a raw bond record to the formatted shape expected by the bonds tables.
export const formatLawFirmBond = (b) => ({
  bond_id: b.bond_id,
  bond_number: b.bond_number || "",
  bond_type: b.bond_type,
  bond_sub_category: b.bond_sub_category,
  principals: b.principals || [],
  estate_name: b.name,
  law_firm_name: b.law_firm_name,
  attorney_name: b.law_firm_user_name,
  underwriting_status: b.underwriting_status || "-",
  bond_amount: b.bond_amount || "0.00",
  status: b.status === "Approved" ? "Active" : (b.status || "Active"),
  created_at: b.created_at,
});

// Pick the filter configuration for bond fetching, handling override vs default.
export const pickFilters = (overrideFilters, selectedLawFirm, filters) => {
  if (overrideFilters) {
    return { ...overrideFilters, lawFirm: [selectedLawFirm] };
  }
  if (filters?.lawFirm?.length) {
    return filters;
  }
  return { ...filters, lawFirm: [selectedLawFirm] };
};

/** Build email body for "Can't find a bond?" portal request (law firm bonds page). */
export function buildCantFindBondEmailPayload({
  lawFirmName,
  requesterName,
  cfBondNumber,
  cfBondType,
  cfName,
  cfPrincipalName,
  cfAction,
  cfOtherInfo,
}) {
  const niceAction = (cfAction || "").toString().trim();
  const actionLabel = niceAction
    ? niceAction.charAt(0).toUpperCase() + niceAction.slice(1)
    : "";
  const detailsTableHtml = `
<table style="width:100%; border-collapse:collapse; margin-top:12px; font-family:Arial, sans-serif; font-size:14px;">
  <thead>
    <tr>
      <th style="text-align:left; padding:8px 10px; border:1px solid #e5e7eb; background:#f9fafb; color:#111827;">Field</th>
      <th style="text-align:left; padding:8px 10px; border:1px solid #e5e7eb; background:#f9fafb; color:#111827;">Value</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Law Firm</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${lawFirmName || "-"}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Requester</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${requesterName || "-"}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Bond Number</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${(cfBondNumber || "").toString().trim() || "-"}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Bond Type</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${cfBondType?.label || "-"}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Name</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${(cfName || "").toString().trim() || "-"}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Principal Name</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${(cfPrincipalName || "").toString().trim() || "-"}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Requested Action</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${actionLabel || "-"}</td>
    </tr>
    <tr>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">Other Information</td>
      <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${(cfOtherInfo || "").toString().trim() || "-"}</td>
    </tr>
  </tbody>
</table>`;
  const contentHtml = `
<p style="font-family:Arial, sans-serif; font-size:14px; color:#111827;">A law firm user submitted a request from the Bond Archives page.</p>
<p style="font-family:Arial, sans-serif; font-size:14px; color:#111827; margin: 0 0 8px 0;"><strong>Law Firm:</strong> ${lawFirmName || "-"}</p>
<p style="font-family:Arial, sans-serif; font-size:14px; color:#111827; margin: 0 0 8px 0;"><strong>Requester:</strong> ${requesterName || "-"}</p>
<p style="font-family:Arial, sans-serif; font-size:14px; color:#111827;">Details:</p>
${detailsTableHtml}
`;
  return {
    subject: "Request: Can't find a bond?",
    content_html: contentHtml,
  };
}
